import ClarityLogo from "@/assets/clarity-logo-light.svg";
import ProposalCoverScrollCue from "@/components/proposal/ProposalCoverScrollCue";
import { PROPOSAL_ACCENT, PROPOSAL_COPY_FONT, PROPOSAL_TEXT } from "@/lib/proposalTheme";
import { PROPOSAL_COVER_FULL_VIEWPORT_CLASS } from "@/lib/proposalCoverLayout";
import { cn } from "@/lib/utils";

type Props = {
  customerName: string;
  proposalRef: string;
  proposalTitle?: string | null;
  preparedForLabel?: string;
  /** Inline logo on non–full-viewport covers; full-viewport uses ProposalStickyClarityLogo instead. */
  showLogo?: boolean;
  /** When true, cover reserves the full first viewport before any proposal content. */
  fullViewport?: boolean;
};

export default function ProposalCustomerHero({
  customerName,
  proposalRef,
  proposalTitle,
  preparedForLabel = "This proposal is prepared for",
  showLogo = true,
  fullViewport = false,
}: Props) {
  const customerBlock = (
    <div className="min-w-0 space-y-3 text-right">
      <p className="text-sm" style={{ fontWeight: 300, color: `${PROPOSAL_TEXT}B3` }}>
        {preparedForLabel}
      </p>
      <h1 className="text-4xl" style={{ fontWeight: 300, lineHeight: 1.2 }}>
        {customerName}
      </h1>
      {proposalTitle?.trim() ? (
        <p className="text-2xl sm:text-3xl" style={{ fontWeight: 300, lineHeight: 1.25 }}>
          {proposalTitle.trim()}
        </p>
      ) : null}
      <p className="text-sm tracking-[0.18em]" style={{ fontWeight: 300, color: PROPOSAL_ACCENT }}>
        {proposalRef}
      </p>
    </div>
  );

  if (fullViewport) {
    return (
      <section
        className={cn("relative isolate block w-full shrink-0 overflow-hidden", PROPOSAL_COVER_FULL_VIEWPORT_CLASS)}
        style={{
          fontFamily: PROPOSAL_COPY_FONT,
          color: PROPOSAL_TEXT,
          letterSpacing: "0.02em",
          minHeight: "max(100svh, 100dvh)",
        }}
        data-testid="proposal-cover-fullscreen"
      >
        <div
          className="absolute top-1/2 right-6 max-w-[min(32rem,calc(100%-3rem))] -translate-y-1/2 text-right sm:right-[8vw]"
          data-testid="proposal-cover-customer-block"
        >
          {customerBlock}
        </div>
        <ProposalCoverScrollCue />
      </section>
    );
  }

  return (
    <section
      className="relative min-h-[40vh]"
      style={{ fontFamily: PROPOSAL_COPY_FONT, color: PROPOSAL_TEXT, letterSpacing: "0.02em" }}
      data-testid="proposal-cover"
    >
      {showLogo ? (
        <div
          className="pointer-events-none fixed top-6 z-20 sm:top-8"
          style={{ right: "max(1.5rem, calc((100vw - 64rem) / 2 + 1.5rem))" }}
        >
          <img
            src={ClarityLogo}
            alt="Clarity"
            className="h-7 w-auto object-contain opacity-95 sm:h-8"
            style={{ maxWidth: "11rem" }}
          />
        </div>
      ) : null}
      <div className="ml-auto mt-40 min-w-0 max-w-[min(100%,32rem)] space-y-3 text-right sm:mt-56">{customerBlock}</div>
    </section>
  );
}
